import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'

/**
 * Catches HID-keyboard (wedge) scanner input anywhere in the app and routes the
 * encoded URL through React Router. Fixes the symptom where scanning on a
 * non-packaging screen (e.g. /, /machines/:stage) silently dropped keystrokes
 * into <body> because no scan input was focused and the AHK helper's Ctrl+L
 * sometimes loses the race to Windows focus-stealing prevention.
 *
 * Coexists with `scripts/qr-scanner.ahk` (the Windows AutoHotkey helper running
 * on packaging-floor machines). Compatibility matrix per scenario:
 *
 *   1. Packaging page + sheet's input focused (existing happy path)
 *      Scanner keys → input.onChange (AHK Input has V flag = visible). Listener
 *      sees target=INPUT → defers. Sheet's 100ms debounce → commit. AHK then
 *      Ctrl+L → Ctrl+C → merge URL → Ctrl+V → Enter (hard reload). Unchanged.
 *
 *   2. Packaging page + body focused
 *      Listener catches scan, navigates via React Router. AHK then Ctrl+L,
 *      reads URL (just updated), merge yields finalTarget == currentUrl →
 *      AHK early-exits at qr-scanner.ahk:158. No double navigation.
 *
 *   3. Non-packaging page + body focused (the reported bug)
 *      Listener catches scan, navigates via React Router. AHK Ctrl+L's
 *      WinActivate may or may not foreground the browser due to Windows focus-
 *      stealing prevention — irrelevant now, the listener already handled it.
 *      If AHK does succeed: currentUrl == scanUrl → early-exit. If it fails:
 *      no-op. Either way, fixed.
 *
 *   4. AHK is dead/uninstalled
 *      Listener is the only path; works identically to scenario 2/3 without AHK.
 *
 * Key invariants this hook preserves:
 *  - `event.key.length === 1` filter rejects modifier-only keys (Ctrl, Alt,
 *    Shift) so AHK's synthetic Ctrl+L → Ctrl+C → Ctrl+V → Enter never
 *    contaminates the buffer.
 *  - Tab is not a navigation trigger here. AHK also terminates Input on Tab,
 *    but only for legacy scanners that emit Tab instead of Enter. Falling back
 *    to AHK in that case is safe — same as scenario 4 above.
 */

// Maco QR codes encode URLs targeting these routes. Hosts other than this list
// are rejected to avoid acting on stray scans (mirrors AHK's AllowedHostMarker).
const SCAN_HOSTS = ['macoapp.ca']
// Path prefixes the scanner is allowed to land on. Mirrors isPackagingPath in
// layout.tsx plus /scanner/:detail (legacy QR redirect target).
const SCAN_PATH_PREFIXES = ['/packaging', '/order/', '/multi-order-skids', '/scanner/']

// HID wedge scanners fire <30ms between keys; human typing rarely <80ms. A gap
// larger than this resets the buffer so manually typed text never accidentally
// triggers navigation.
const MAX_INTER_KEY_MS = 60
// Smallest plausible scanner output (`?skid=X`). Shorter Enter-terminated
// sequences are dropped to avoid acting on accidental keyboard chords.
const MIN_SCAN_LENGTH = 8
// Idle time after which a partial buffer is forgotten. Same TTL as AHK's
// ScanTimeoutSec / 30 → much shorter; we only care about a live scan.
const BUFFER_RESET_MS = 500

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  // `closest` walks up parents too, so cursors inside nested contenteditable
  // regions still count. jsdom doesn't implement `isContentEditable` (returns
  // false even when contenteditable="true"), so we read the attribute directly.
  if (target.closest('[contenteditable]:not([contenteditable="false"])')) return true
  return false
}

interface ScanDestination {
  pathname: string
  search: string
}

/** Parse a scanned buffer and return a same-origin destination, or null. */
export function parseScanBuffer(buf: string): ScanDestination | null {
  const trimmed = buf.trim()
  if (trimmed.length < MIN_SCAN_LENGTH) return null

  // Accept absolute URLs only — the scanner emits a full URL, and a relative
  // path could be the result of stray keystrokes that happen to start with "/".
  if (!/^https?:\/\//i.test(trimmed)) return null

  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return null
  }

  if (!SCAN_HOSTS.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`))) return null
  if (!SCAN_PATH_PREFIXES.some((p) => url.pathname.startsWith(p))) return null

  return { pathname: url.pathname, search: url.search }
}

// Always stripped from the current URL even if the scan doesn't carry them.
// Reason: a packaging session has ONE active scan target at a time — either a
// bundle or a skid, never both — so any leftover key from the previous scan
// would either confuse the resolver or pop the wrong sheet. Mirrors AHK's
// `stripKeys := "|bundle|skid|"` initial set.
const ALWAYS_STRIPPED_KEYS = ['bundle', 'skid'] as const

/**
 * Merge a scanned URL onto the current location, preserving operator-set filters.
 *
 * Behaviour mirrors `__MergeScanUrl` in scripts/qr-scanner.ahk (Apr 2026 version):
 *  - Different pathname → return scan as-is. The scan moves the operator to a
 *    new route; current filters don't apply.
 *  - Same pathname → keep every current query key the scan doesn't touch
 *    (search, ship-date, detail, orders, cart, ...). Always drop bundle+skid
 *    from current. Append scan keys with the scan's values.
 *
 * Returns the destination to navigate to.
 */
export function mergeScanWithCurrent(
  scan: ScanDestination,
  current: { pathname: string; search: string }
): ScanDestination {
  if (scan.pathname !== current.pathname) return scan

  const scanParams = new URLSearchParams(scan.search)
  const merged = new URLSearchParams(current.search)
  for (const key of ALWAYS_STRIPPED_KEYS) merged.delete(key)
  for (const key of scanParams.keys()) merged.delete(key)
  for (const [key, value] of scanParams) merged.append(key, value)

  const search = merged.toString()
  return { pathname: scan.pathname, search: search ? `?${search}` : '' }
}

export function useGlobalScanner(): void {
  const navigate = useNavigate()
  const bufRef = useRef('')
  const lastKeyAtRef = useRef(0)
  const resetTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const resetSoon = () => {
      if (resetTimerRef.current != null) window.clearTimeout(resetTimerRef.current)
      resetTimerRef.current = window.setTimeout(() => {
        bufRef.current = ''
      }, BUFFER_RESET_MS)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      // Real scan inputs (BundleScanSheet, bundle-skid-id-cell, address bar
      // autocomplete) handle their own input. Don't double-process.
      if (isEditableTarget(event.target)) {
        bufRef.current = ''
        return
      }

      const now = Date.now()
      const gap = now - lastKeyAtRef.current
      lastKeyAtRef.current = now

      if (event.key === 'Enter') {
        const buf = bufRef.current
        bufRef.current = ''
        if (resetTimerRef.current != null) {
          window.clearTimeout(resetTimerRef.current)
          resetTimerRef.current = null
        }

        const parsed = parseScanBuffer(buf)
        if (!parsed) return

        const dest = mergeScanWithCurrent(parsed, {
          pathname: window.location.pathname,
          search: window.location.search
        })

        // Skip when the merge resolves to the URL we're already on — avoids a
        // redundant navigation that would re-trigger nuqs effects and reset
        // unrelated panel state.
        if (
          dest.pathname === window.location.pathname &&
          dest.search === window.location.search
        ) {
          event.preventDefault()
          return
        }

        event.preventDefault()
        navigate({ pathname: dest.pathname, search: dest.search })
        return
      }

      // A pause longer than wedge inter-key timing means the previous buffer
      // belonged to a separate event (or human typing); drop it. Applied only to
      // character keys — the terminating Enter can lag the last char under
      // RDP/Citrix input jitter and must NOT wipe a finished buffer; genuinely
      // stale buffers are cleared by the BUFFER_RESET_MS idle timer instead.
      if (gap > MAX_INTER_KEY_MS) bufRef.current = ''

      // Only printable single characters contribute to the buffer. Modifier-
      // only events (Shift, Control, Alt) and navigation keys are ignored.
      if (event.key.length === 1) {
        bufRef.current += event.key
        resetSoon()
      }
    }

    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true })
      if (resetTimerRef.current != null) window.clearTimeout(resetTimerRef.current)
    }
  }, [navigate])
}
